import { Tabs, TabsList, TabsTrigger } from "@appretio/ui-kit";
import {
  INTAKE_SOURCES,
  type IntakeSource,
  isIntakeSource
} from "../cubeDataTable/utils/intakeUtils";

/** Props for {@link IntakeSourceTabs}. */
export interface IntakeSourceTabsProps {
  /** The source whose rows the grid shows; none before data loads. */
  value?: IntakeSource;
  /** Called with the source the planner picked. */
  onChange: (source: IntakeSource) => void;
  /** The sources the loaded data can switch to; a tab outside it is disabled. */
  availableSources: readonly IntakeSource[];
  /** The tabs shown, in toggle order; every source by default. */
  sources?: readonly IntakeSource[];
  /** Disables every tab, e.g. while a swap is in flight. */
  disabled?: boolean;
  /** Reason shown for the whole group when `disabled`. */
  title?: string;
}

/** The intake source toggle: one tab per source, disabled where the data lacks it. */
export function IntakeSourceTabs({
  value,
  onChange,
  availableSources,
  sources = INTAKE_SOURCES,
  disabled = false,
  title
}: IntakeSourceTabsProps) {
  return (
    <Tabs
      // "" keeps the tabs controlled with no tab selected.
      value={value ?? ""}
      onValueChange={(next) => {
        if (isIntakeSource(next)) onChange(next);
      }}
      disabled={disabled}
      title={title}
    >
      <TabsList>
        {sources.map((source) => {
          const unavailable =
            source !== value && !availableSources.includes(source);
          return (
            // A disabled button swallows hover, so the reason sits on a wrapper.
            <span
              key={source}
              title={
                unavailable
                  ? `${source} intake not available for this selection`
                  : undefined
              }
            >
              {/* A trigger's own `disabled` replaces the group's, so it is repeated. */}
              <TabsTrigger value={source} disabled={disabled || unavailable}>
                {source}
              </TabsTrigger>
            </span>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
